import React, { createContext, useContext, useEffect, useState } from 'react'
import { applyCommunityTheme, communityThemeValues, syncBrowserBar } from './theme'
import { MEMBER_APPEARANCE_EVENT, appearanceOf, applyDark, isDark, readMemberAppearance, watchSystemTheme } from './appearance'
import type { CommunityAppearance } from './appearance'
import { configureImages } from './images'
import { applyCommunityHead, serverOwnsTitleFor } from './utils/head'
import type { CurrencyFormat } from './money'

/* The community's money format; it lives with the formatter (U4). */
export type { CurrencyFormat } from './money'

/** F-143: Campaigns and Automações paused (or close to it) for unsubscribes or spam complaints. */
export interface SendingReputation {
  suspended: boolean
  /** End of the suspension; null with `untilLifted` = until Memberfy lifts it. */
  until: string | null
  untilLifted?: boolean
  reason: 'unsubscribes' | 'complaints' | string | null
  level: number | null
  alert?: boolean
  tips?: string[]
  /** The numbers behind the banner (team only): this community's window and thresholds, in percent. */
  rates?: {
    windowDays: number
    delivered: number
    unsubscribes: number
    complaints: number
    unsubscribeRate: number
    complaintRate: number
    minDelivered: number
    alert: { unsubscribeRate: number; complaintRate: number }
    suspend: { unsubscribeRate: number; complaintRate: number }
  } | null
}

export interface Community {
  id: string
  name: string
  description: string
  logoUrl: string | null
  faviconUrl?: string | null
  ogImageUrl?: string | null
  metaTitle?: string | null
  metaDescription?: string | null
  metaKeywords?: string | null
  themeColor?: string | null
  /** The community's color palette. Null means the product's default. */
  themePalette?: unknown
  /** F-191: light, dark or automatic, the dark logo and the SuperAdmin's switch. */
  appearance?: CommunityAppearance | null
  domain: string
  /** Where images are served from. Optional: an older API does not send it. */
  media?: { origin: string | null; imgix: string | null } | null
  slug: string
  googleAnalyticsId?: string | null
  /** F-207: the search engines' verification codes (owner and admin edit them; the server writes the tags). */
  googleSiteVerification?: string | null
  bingSiteVerification?: string | null
  signupEnabled: boolean
  /** F-150: with `signupEnabled`, a signup becomes a request the team approves. */
  signupRequiresApproval?: boolean
  signupModel: 'FREE' | 'PAID'
  /** F-204: the age to have an account here, above the platform's 16; null = 16. */
  minimumAge?: number | null
  /** F-204: the owner declared the community serves minors. */
  servesMinors?: boolean
  guestAccess?: boolean
  contentModeration?: boolean
  payoutEnabled?: boolean
  globalRanking?: boolean
  /** F-70: can the viewer use the AI assistant (MCP)? False signed out; always true for the owner. */
  mcpAllowed?: boolean
  /** F-70 switches, sent to owner/admin/moderator/finance only. */
  mcpMembers?: boolean
  mcpStaff?: boolean
  mcpAdmins?: boolean
  /** F-101: can the viewer hold personal API keys here? False signed out; always true for the team. */
  canUseApiKeys?: boolean
  /** F-101: the owner's switch for members' API keys, sent to the team only. */
  memberApiKeysEnabled?: boolean
  /** F-107: "Permitir envio de Drops nos comentários" (owner/admin switch, off by default). */
  dropsInComments?: boolean
  /** F-34: whether the plan keeps the "Powered by Memberfy" mark in the Sidebar. */
  branding?: { showMemberfyMark?: boolean }
  active: boolean
  createdAt: string
  deletedAt: string | null
  country: string
  currency: string
  language: string
  timezone: string
  currencyFormat?: CurrencyFormat
  stats: {
    members: number
    contents: number
    courses: number
  }
  /** The session's member here, mirrored by App from AuthContext. */
  userMember: { role?: string } | null
  /**
   * F-143, team only (absent for members): the community's sending reputation.
   * `alert` comes only to owner and admin; `tips` are keys the app translates.
   */
  sendingReputation?: SendingReputation | null
  settings?: {
    defaultLanguage?: string
    allowedLanguages?: string[]
    defaultUserRole?: string
    contentModeration?: string
    eventRegistrationEnabled?: boolean
    globalRanking?: boolean
  }
}

interface CommunityContextType {
  community: Community | null
  setCommunity: React.Dispatch<React.SetStateAction<Community | null>>
  isCommunityFound: boolean
  setIsCommunityFound: (found: boolean) => void
}

const CommunityContext = createContext<CommunityContextType | undefined>(undefined)

export function CommunityProvider({ children }: { children: React.ReactNode }) {
  const [community, setCommunity] = useState<Community | null>(null)
  const [isCommunityFound, setIsCommunityFound] = useState<boolean>(true)

  // F-191 phase 4: the active theme (`appearance.theme`; an older API sends
  // only `themePalette`): palette, menu and header colors, corners, density
  // and fonts. Today's defaults leave index.css answering, so a community that
  // never changed its look looks exactly as it always did. Nothing is applied
  // while the community is unresolved (the boot screen keeps the product's).
  const themeKey = community ? JSON.stringify([community.appearance?.theme ?? null, community.themePalette ?? null, community.themeColor ?? null]) : null
  useEffect(() => {
    if (!community) return
    applyCommunityTheme(communityThemeValues(community))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the theme changes, not on every community object
  }, [themeKey])

  /* F-191 phase 1: the community's mode, or the member's choice in this
     browser, as the `dark` class on <html>. Re-applied when the device theme
     changes (automatic) and when the member picks another option. While the
     community is unresolved the boot script's guess (index.html) stays. */
  const appearance = community ? appearanceOf(community.appearance) : null
  const appearanceKey = appearance ? `${appearance.effectiveMode}:${appearance.darkModeAllowed}` : null
  useEffect(() => {
    if (!appearance) return
    const apply = () => {
      applyDark(isDark(appearance, readMemberAppearance()), appearance)
      syncBrowserBar()
    }
    apply()
    const stopWatching = watchSystemTheme(apply)
    window.addEventListener(MEMBER_APPEARANCE_EVENT, apply)
    return () => {
      stopWatching()
      window.removeEventListener(MEMBER_APPEARANCE_EVENT, apply)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the mode or the switch changes
  }, [appearanceKey])

  /* Where images are served from, straight from the server. The build-time env
     answers until this lands, and keeps answering if the field is absent — an
     older API must not leave the app without images. */
  const media = community?.media ?? null
  const mediaOrigin = media?.origin
  const mediaImgix = media?.imgix
  useEffect(() => {
    configureImages(media)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the hosts change, not on every community object
  }, [mediaOrigin, mediaImgix])

  /* Tab icon, title and share card. Depends on `configureImages` above having
     run, otherwise the favicon comes off the origin instead of imgix — which is
     why it sits after it and not before.

     **While the community is unresolved the head is left alone.** The server
     already wrote the right one into the HTML, and `applyCommunityHead(null)`
     deliberately returns to the scaffold — which, running on mount, wiped the
     correct title and icon and only put them back once the API answered. That
     was the "Memberfy" and the generic icon showing up during load.

     `community === null` with `isCommunityFound` still `true` is the loading
     state. Once it turns `false`, going back to the default is right: the error
     screen must not inherit anybody's brand. */
  useEffect(() => {
    if (!community && isCommunityFound) return
    /* Icon and card are always applied; the title only if the server has not
       written a better one for this route. */
    applyCommunityHead(community, { keepTitle: serverOwnsTitleFor(window.location.pathname) })
  }, [
    isCommunityFound,
    community?.faviconUrl, community?.ogImageUrl, community?.metaTitle,
    community?.metaDescription, community?.metaKeywords, community?.name, community?.domain
  ])

  return (
    <CommunityContext.Provider value={{ 
      community, 
      setCommunity, 
      isCommunityFound, 
      setIsCommunityFound 
    }}>
      {children}
    </CommunityContext.Provider>
  )
}

/** The community context, or undefined outside the provider (no throw). */
export function useOptionalCommunity() {
  return useContext(CommunityContext)?.community ?? null
}

export function useCommunity() {
  const context = useContext(CommunityContext)
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider')
  }
  return context
}